import { useState, useEffect } from 'react';
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import DataTable from 'react-data-table-component';
// @mui
import { Grid, Container, Typography, Card, CardContent, Box, Avatar, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem } from '@mui/material';
// components
import config from "../../config";
import { encryptData } from '../../utils/cryptoUtils';
import * as dashboardActions from "../../actions/dashboardActions";

const LeadSummaryDashboard = (props) => {
  const { actions } = props;
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors }, setValue } = useForm();

  const [summaryData, setSummaryData] = useState({
    leadsAdded: 0,
    bureauPulled: 0,
    fiDone: 0,
    fiPending: 0,
    sentToCredit: 0,
    camApproved: 0,
    salesRejection: 0,
    creditReject: 0,
    pendingMoreInfo: 0,
    disbursed: 0
  });

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [leadsList, setLeadsList] = useState([]);
  const [showLeadsList, setShowLeadsList] = useState(false);

  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [currentStartDate, setCurrentStartDate] = useState('');
  const [currentEndDate, setCurrentEndDate] = useState('');

  // Loans Details State
  const [selectedYearMonth, setSelectedYearMonth] = useState('');
  const [loansData, setLoansData] = useState({
    notPaid: 0,
    partiallyPaid: 0,
    fullyPaid: 0,
    enach: 0,
    dynamicQR: 0,
    staticQR: 0,
    other: 0
  });
  const [selectedLoanCategory, setSelectedLoanCategory] = useState(null);
  const [loansList, setLoansList] = useState([]);
  const [showLoansList, setShowLoansList] = useState(false);
  const [loansTotalRows, setLoansTotalRows] = useState(0);
  const [loansCurrentPage, setLoansCurrentPage] = useState(1);
  const [loansPageSize, setLoansPageSize] = useState(10);

  // Filter State
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [rms, setRMs] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedRM, setSelectedRM] = useState('');
  const [selectedUser, setSelectedUser] = useState('');

  useEffect(() => {
    // Check user is logged-in or not
    const authToken = localStorage.getItem('authToken');
    if (authToken === null || authToken === "") {
      navigate('/login');
    }
  }, []);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load filter dropdowns
  useEffect(() => {
    loadFilterDropdowns();
  }, []);

  const loadFilterDropdowns = async () => {
    try {
      const requestData = encryptData({});

      // Load locations
      const locationsResponse = await actions.getFilterLocations(requestData, false);
      if (locationsResponse.data.success === 1) {
        setLocations(locationsResponse.data.data || []);
      }

      // Load RMs
      const rmsResponse = await actions.getFilterRMs(requestData, false);
      if (rmsResponse.data.success === 1) {
        setRMs(rmsResponse.data.data || []);
      }

      // Load Users
      const usersResponse = await actions.getFilterUsers(requestData, false);
      if (usersResponse.data.success === 1) {
        setUsers(usersResponse.data.data || []);
      }
    } catch (error) {
      console.error('Error loading filter dropdowns:', error);
    }
  };

  const handleOpenFilterModal = () => {
    setFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setFilterModalOpen(false);
  };

  const handleApplyFilter = () => {
    setFilterModalOpen(false);

    // Refresh both summaries with filters
    const startDate = document.querySelector('input[name="start_date"]')?.value || '';
    const endDate = document.querySelector('input[name="end_date"]')?.value || '';
    if (startDate && endDate) {
      fetchSummaryData({ start_date: startDate, end_date: endDate });
    }

    if (selectedYearMonth) {
      fetchLoansData(selectedYearMonth);
    }

    // Reset lists
    setShowLeadsList(false);
    setShowLoansList(false);
  };

  const handleResetFilter = () => {
    setSelectedLocation('');
    setSelectedRM('');
    setSelectedUser('');
    setFilterModalOpen(false);

    // Refresh both summaries without filters
    const startDate = document.querySelector('input[name="start_date"]')?.value || '';
    const endDate = document.querySelector('input[name="end_date"]')?.value || '';
    if (startDate && endDate) {
      fetchSummaryData({ start_date: startDate, end_date: endDate });
    }

    if (selectedYearMonth) {
      fetchLoansData(selectedYearMonth);
    }

    // Reset lists
    setShowLeadsList(false);
    setShowLoansList(false);
  };

  useEffect(() => {
    const today = new Date();
    const todayFormatted = formatDate(today);

    setValue('start_date', todayFormatted);
    setValue('end_date', todayFormatted);

    // Set current year-month for loans section
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const yearMonth = `${year}-${month}`;
    setSelectedYearMonth(yearMonth);

    // Fetch initial data
    fetchSummaryData({ start_date: todayFormatted, end_date: todayFormatted });
    fetchLoansData(yearMonth);
  }, [setValue]);

  const fetchSummaryData = async (data) => {
    const params = {
      'start': '0',
      'length': config.MAX_RECORDS,
      'start_date': data.start_date,
      'end_date': data.end_date
    };

    // Add filter parameters if selected
    if (selectedLocation) {
      params.location_id = selectedLocation;
    }
    if (selectedRM) {
      params.rm_id = selectedRM;
    }
    if (selectedUser) {
      params.filter_user_id = selectedUser;
    }

    const requestData = encryptData(params);

    try {
      const response = await actions.getLeadSummaryData(requestData, false);
      if (response.data.success === 1) {
        const results = response.data.data;
        setSummaryData({
          leadsAdded: results.leads_added || 0,
          bureauPulled: results.bureau_pulled || 0,
          fiDone: results.fi_done || 0,
          fiPending: results.fi_pending || 0,
          sentToCredit: results.sent_to_credit || 0,
          camApproved: results.cam_approved || 0,
          salesRejection: results.sales_rejection || 0,
          creditReject: results.credit_reject || 0,
          pendingMoreInfo: results.pending_more_info || 0,
          disbursed: results.disbursed || 0
        });
      }
    } catch (error) {
      console.error("Promise rejected - getLeadSummaryData:", error);
      toast.error("Error fetching summary data", {
        position: "top-right",
      });
    }
  };

  const handleFilterSubmit = (data) => {
    setShowLeadsList(false);
    setSelectedCategory(null);
    setCurrentPage(1);
    setCurrentStartDate('');
    setCurrentEndDate('');
    fetchSummaryData(data);
  };

  const fetchLeadDetails = async (category, startDate, endDate, page, size) => {
    const params = {
      'start': '0',
      'length': config.MAX_RECORDS,
      'category': category,
      'start_date': startDate || formatDate(new Date()),
      'end_date': endDate || formatDate(new Date()),
      'page': page,
      'size': size
    };

    // Add filter parameters if selected
    if (selectedLocation) {
      params.location_id = selectedLocation;
    }
    if (selectedRM) {
      params.rm_id = selectedRM;
    }
    if (selectedUser) {
      params.filter_user_id = selectedUser;
    }

    const requestData = encryptData(params);

    try {
      const response = await actions.getLeadSummaryDetails(requestData, false);
      if (response.data.success === 1) {
        const results = response.data.data;
        setLeadsList(results.leads || []);
        setTotalRows(results.count || 0);
      }
    } catch (error) {
      console.error("Promise rejected - getLeadSummaryDetails:", error);
      toast.error("Error fetching lead details", {
        position: "top-right",
      });
    }
  };

  const handleBoxClick = async (category, startDate, endDate) => {
    setSelectedCategory(category);
    setShowLeadsList(true);
    setCurrentStartDate(startDate || formatDate(new Date()));
    setCurrentEndDate(endDate || formatDate(new Date()));
    setCurrentPage(1); // Reset to first page when clicking a new box

    await fetchLeadDetails(category, startDate, endDate, 1, pageSize);
  };

  // Handle pagination changes
  useEffect(() => {
    if (selectedCategory && showLeadsList && currentStartDate && currentEndDate) {
      fetchLeadDetails(selectedCategory, currentStartDate, currentEndDate, currentPage, pageSize);
    }
  }, [currentPage, pageSize]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = async (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  // Loans Details Functions
  const fetchLoansData = async (yearMonth) => {
    const [year, month] = yearMonth.split('-');
    const params = {
      'start': '0',
      'length': config.MAX_RECORDS,
      'year': year,
      'month': month
    };

    // Add filter parameters if selected
    if (selectedLocation) {
      params.location_id = selectedLocation;
    }
    if (selectedRM) {
      params.rm_id = selectedRM;
    }
    if (selectedUser) {
      params.filter_user_id = selectedUser;
    }

    const requestData = encryptData(params);

    try {
      const response = await actions.getLoansSummaryData(requestData, false);
      if (response.data.success === 1) {
        const results = response.data.data;
        setLoansData({
          notPaid: results.not_paid || 0,
          partiallyPaid: results.partially_paid || 0,
          fullyPaid: results.fully_paid || 0,
          enach: results.enach || 0,
          dynamicQR: results.dynamic_qr || 0,
          staticQR: results.static_qr || 0,
          other: results.other || 0
        });
      }
    } catch (error) {
      console.error("Promise rejected - getLoansSummaryData:", error);
      toast.error("Error fetching loans summary data", {
        position: "top-right",
      });
    }
  };

  const handleYearMonthChange = (e) => {
    const yearMonth = e.target.value;
    setSelectedYearMonth(yearMonth);
    setShowLoansList(false);
    setSelectedLoanCategory(null);
    fetchLoansData(yearMonth);
  };

  const fetchLoansDetails = async (category, yearMonth, page, size) => {
    const [year, month] = yearMonth.split('-');
    const params = {
      'start': '0',
      'length': config.MAX_RECORDS,
      'category': category,
      'year': year,
      'month': month,
      'page': page,
      'size': size
    };

    // Add filter parameters if selected
    if (selectedLocation) {
      params.location_id = selectedLocation;
    }
    if (selectedRM) {
      params.rm_id = selectedRM;
    }
    if (selectedUser) {
      params.filter_user_id = selectedUser;
    }

    const requestData = encryptData(params);

    try {
      const response = await actions.getLoansDetails(requestData, false);
      if (response.data.success === 1) {
        const results = response.data.data;
        setLoansList(results.loans || []);
        setLoansTotalRows(results.count || 0);
      }
    } catch (error) {
      console.error("Promise rejected - getLoansDetails:", error);
      toast.error("Error fetching loan details", {
        position: "top-right",
      });
    }
  };

  const handleLoanBoxClick = async (category, yearMonth) => {
    setSelectedLoanCategory(category);
    setShowLoansList(true);
    setLoansCurrentPage(1);

    await fetchLoansDetails(category, yearMonth, 1, loansPageSize);
  };

  // Handle loans pagination changes
  useEffect(() => {
    if (selectedLoanCategory && showLoansList && selectedYearMonth) {
      fetchLoansDetails(selectedLoanCategory, selectedYearMonth, loansCurrentPage, loansPageSize);
    }
  }, [loansCurrentPage, loansPageSize]);

  const handleLoansPageChange = (page) => {
    setLoansCurrentPage(page);
  };

  const handleLoansPerRowsChange = async (newPageSize) => {
    setLoansPageSize(newPageSize);
    setLoansCurrentPage(1);
  };

  const leadColumns = [
    {
      name: 'Lead Name',
      selector: (row) => (
        <a href={`/dashboard/edit-lead/${btoa(String(row.lead_id))}`} style={{ color: 'black' }} 
        target="_blank" rel="noopener noreferrer">
          {row.lead_name}
        </a>
      ),
      sortable: true,
    },
    {
      name: 'Mobile',
      selector: (row) => row.mobile,
      sortable: true,
    },
    {
      name: 'Lead ID', 
      selector: (row) => (
        <a
        href={`/dashboard/edit-lead/${btoa(String(row.lead_id))}`}
        style={{ color: 'black' }}
        target="_blank"
        rel="noopener noreferrer"
      >
        {row.lead_id}
      </a>
      ),
      sortable: true,
    },

    ...(
     selectedCategory === "bureau_pulled" 
        ? [{
          name: 'Pulled For',
          selector: (row) => {
            const name = row?.section_name?.trim();
            return name
              ? name.charAt(0).toUpperCase() + name.slice(1)
              : '-';
          },
          sortable: true,
        }]
        : []
    ),

     ...(
     selectedCategory === "cam_approved" 
        ? [{
          name: 'Sales User',
          selector: (row) => row?.sales_user_name || '-',
        }]
        : []
    ),

    {
      name: ( selectedCategory === "bureau_pulled" ? 'Pulled Date' : (selectedCategory === "cam_approved" ? 'Approved Date' : (selectedCategory === "sent_to_credit" ? 'Sent to Credit Date' : (selectedCategory === "sales_rejection" ? 'Rejected Date' : (selectedCategory === "credit_reject" ? 'Credit Rejected Date' : (selectedCategory === "disbursed" ? 'Disbursed Date' : 'Created Date')))))),
      selector: (row) => row.created_date,
      sortable: true,
    }
  ];

  const loanColumns = [
    {
      name: 'Agreement No',
      selector: (row) => row.agreement_no,
      sortable: true,
    },
    {
      name: 'Customer Name',
      selector: (row) => row.customer_name,
      sortable: true,
    },
    {
      name: 'Due Date',
      selector: (row) => row.due_date,
      sortable: true,
    },
    {
      name: 'Due Amount',
      selector: (row) => row.due_amount,
      sortable: true,
    },
    {
      name: 'Paid Amount',
      selector: (row) => row.paid_amount,
      sortable: true,
    },
     {
      name: 'Payment Date',
      selector: (row) => row.payment_date,
      sortable: true,
    },
    {
      name: 'Payment Status',
      selector: (row) => row.payment_status,
      sortable: true,
    },
    {
      name: 'Mode of Payment',
      selector: (row) => row?.payment_type.replace(/_/g, ' ').toUpperCase() || '-', 
      sortable: true,
    }
  ];

  const SummaryBox = ({ title, count, category, icon, iconColor, subtitle }) => {
    const isSelected = selectedCategory === category;

    return (
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <Card
          sx={{
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#f8f9fa',
              borderColor: '#1976d2'
            },
            backgroundColor: isSelected ? '#f0f7ff' : '#fff',
            height: '100%',
            border: isSelected ? '2px solid #1976d2' : '1px solid #dfe1e6',
            borderRadius: '8px',
            transition: 'all 0.2s ease',
            boxShadow: 'none'
          }}
          onClick={() => {
            const startDate = document.querySelector('input[name="start_date"]')?.value;
            const endDate = document.querySelector('input[name="end_date"]')?.value;
            handleBoxClick(category, startDate, endDate);
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              {/* Icon Circle */}
              <Box
                sx={{
                  bgcolor: iconColor || '#1976d2',
                  width: 40,
                  height: 40,
                  minWidth: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  flexShrink: 0
                }}
              >
                {icon}
              </Box>

              {/* Content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    fontWeight: 600,
                    color: '#172b4d',
                    fontSize: '1.75rem',
                    lineHeight: 1.2,
                    mb: 0.5
                  }}
                >
                  {count}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#172b4d',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    lineHeight: 1.4,
                    mb: 0.25
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#6b778c',
                    fontSize: '0.75rem',
                    lineHeight: 1.4,
                    display: 'block'
                  }}
                >
                  {subtitle || 'Based on selected date range'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const LoanSummaryBox = ({ title, count, category, icon, iconColor, subtitle, subCategories }) => {
    const isSelected = selectedLoanCategory === category;

    return (
      <Grid item xs={12} sm={6} md={6} lg={4}>
        <Card
          sx={{
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#f8f9fa',
              borderColor: '#1976d2'
            },
            backgroundColor: isSelected ? '#f0f7ff' : '#fff',
            height: '100%',
            border: isSelected ? '2px solid #1976d2' : '1px solid #dfe1e6',
            borderRadius: '8px',
            transition: 'all 0.2s ease',
            boxShadow: 'none'
          }}
          onClick={() => handleLoanBoxClick(category, selectedYearMonth)}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              {/* Icon Circle */}
              <Box
                sx={{
                  bgcolor: iconColor || '#dce4ea',
                  width: 40,
                  height: 40,
                  minWidth: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  flexShrink: 0
                }}
              >
                {icon}
              </Box>

              {/* Content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    fontWeight: 600,
                    color: '#172b4d',
                    fontSize: '1.75rem',
                    lineHeight: 1.2,
                    mb: 0.5
                  }}
                >
                  {count}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#172b4d',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    lineHeight: 1.4,
                    mb: 0.25
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#6b778c',
                    fontSize: '0.75rem',
                    lineHeight: 1.4,
                    display: 'block'
                  }}
                >
                  {subtitle}
                </Typography>

                {/* Sub-categories */}
                {subCategories && subCategories.length > 0 && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #dfe1e6' }}>
                    <Grid container spacing={1}>
                      {subCategories.map((subCat, index) => (
                        <Grid item xs={6} key={index}>
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLoanBoxClick(subCat.category, selectedYearMonth);
                            }}
                            sx={{
                              cursor: 'pointer',
                              p: 1,
                              borderRadius: '4px',
                              '&:hover': {
                                backgroundColor: '#e8f0fe'
                              },
                              backgroundColor: selectedLoanCategory === subCat.category ? '#e8f0fe' : 'transparent'
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#6b778c',
                                fontSize: '0.7rem',
                                display: 'block'
                              }}
                            >
                              {subCat.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: '#172b4d',
                                fontSize: '0.875rem'
                              }}
                            >
                              {subCat.count}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <>
      <Helmet>
        <title>Summary Dashboard | Ckers</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ py: 3, backgroundColor: '#fafbfc', minHeight: '100vh' }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#172b4d', mb: 0.5, fontSize: '1.5rem' }}>
              Summary Dashboard
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b778c', fontSize: '0.875rem' }}>
              Overview of lead metrics and status distribution
            </Typography>
          </Box>
          <IconButton
            onClick={handleOpenFilterModal}
            sx={{
              backgroundColor: '#0052cc',
              color: '#fff',
              '&:hover': { backgroundColor: '#0747a6' },
              width: 40,
              height: 40
            }}
            title="Filter"
          >
            <span style={{ fontSize: '20px' }}>🔍</span>
          </IconButton>
        </Box>

        {/* Date Filters */}
        <Box sx={{ mb: 3 }}>
          <Card sx={{ p: 2.5, backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dfe1e6', boxShadow: 'none' }}>
            <form onSubmit={handleSubmit(handleFilterSubmit)}>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={3.5}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#172b4d', fontSize: '0.875rem' }}>
                      Start Date
                    </Typography>
                    <input
                      type='date'
                      name='start_date'
                      className='form-control'
                      {...register('start_date', { required: true })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        border: '1px solid #dfe1e6',
                        fontSize: '14px',
                        color: '#172b4d',
                        backgroundColor: '#fafbfc'
                      }}
                    />
                    {errors.start_date && (
                      <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, fontSize: '0.75rem' }}>
                        This field is required.
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3.5}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#172b4d', fontSize: '0.875rem' }}>
                      End Date
                    </Typography>
                    <input
                      type='date'
                      name='end_date'
                      className='form-control'
                      {...register('end_date', { required: true })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        border: '1px solid #dfe1e6',
                        fontSize: '14px',
                        color: '#172b4d',
                        backgroundColor: '#fafbfc'
                      }}
                    />
                    {errors.end_date && (
                      <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, fontSize: '0.75rem' }}>
                        This field is required.
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <button
                    type="submit"
                    className='btn btn-primary'
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: 500,
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: '#0052cc',
                      color: '#fff',
                      cursor: 'pointer'
                    }}
                  >
                    Filter
                  </button>
                </Grid>
              </Grid>
            </form>
          </Card>
        </Box>

        {/* Summary Boxes */}
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          <SummaryBox
            title="Leads Added"
            count={summaryData.leadsAdded}
            category="leads_added"
            icon="👤"
            iconColor="#dce4ea"
            subtitle="Total leads created"
          />
          <SummaryBox
            title="Bureau Pulled"
            count={summaryData.bureauPulled}
            category="bureau_pulled"
            icon="📊"
            iconColor="#dce4ea"
            subtitle="Credit reports fetched"
          />
          <SummaryBox
            title="FI Done"
            count={summaryData.fiDone}
            category="fi_done"
            icon="✅"
            iconColor="#dce4ea"
            subtitle="Field investigations completed"
          />
          <SummaryBox
            title="FI Pending"
            count={summaryData.fiPending}
            category="fi_pending"
            icon="⏳"
            iconColor="#dce4ea"
            subtitle="Awaiting field investigation"
          />
          <SummaryBox
            title="Sent To Credit"
            count={summaryData.sentToCredit}
            category="sent_to_credit"
            icon="📤"
            iconColor="#dce4ea"
            subtitle="Forwarded to credit team"
          />
          <SummaryBox
            title="CAM Approved"
            count={summaryData.camApproved}
            category="cam_approved"
            icon="👍"
            iconColor="#dce4ea"
            subtitle="Credit approval completed"
          />
          <SummaryBox
            title="Sales Rejection"
            count={summaryData.salesRejection}
            category="sales_rejection"
            icon="❌"
            iconColor="#dce4ea"
            subtitle="Rejected by sales team"
          />
          <SummaryBox
            title="Credit Reject"
            count={summaryData.creditReject}
            category="credit_reject"
            icon="🚫"
            iconColor="#dce4ea"
            subtitle="Rejected by credit team"
          />
          <SummaryBox
            title="Disbursed"
            count={summaryData.disbursed}
            category="disbursed"
            icon="💰"
            iconColor="#dce4ea"
            subtitle="Successfully disbursed"
          />
        </Grid>

        {/* Leads List Table */}
        {showLeadsList && (
          <Box sx={{ mt: 3 }}>
            <Card sx={{ borderRadius: '8px', border: '1px solid #dfe1e6', boxShadow: 'none', overflow: 'hidden' }}>
              <Box sx={{ p: 2.5, backgroundColor: '#fafbfc', borderBottom: '1px solid #dfe1e6' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#172b4d', fontSize: '1rem' }}>
                  {selectedCategory ? selectedCategory.replace(/_/g, ' ').toUpperCase() : 'Lead Details'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b778c', mt: 0.5, fontSize: '0.875rem' }}>
                  Total Records: {totalRows}
                </Typography>
              </Box>
              <Box sx={{ p: 0 }}>
                <DataTable
                  columns={leadColumns}
                  data={leadsList}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  paginationPerPage={pageSize}
                  onChangeRowsPerPage={handlePerRowsChange}
                  paginationDefaultPage={currentPage}
                  onChangePage={handlePageChange}
                  customStyles={{
                    headRow: {
                      style: {
                        backgroundColor: '#fafbfc',
                        borderBottom: '1px solid #dfe1e6',
                        fontWeight: 600,
                        fontSize: '13px',
                        color: '#172b4d',
                        minHeight: '42px'
                      },
                    },
                    rows: {
                      style: {
                        fontSize: '13px',
                        color: '#172b4d',
                        minHeight: '48px',
                        borderBottom: '1px solid #f4f5f7',
                        '&:hover': {
                          backgroundColor: '#f4f5f7',
                          cursor: 'pointer'
                        }
                      },
                    },
                  }}
                />
              </Box>
            </Card>
          </Box>
        )}

        {/* Loans Details Section */}
        <Box sx={{ mt: 6, pt: 4, borderTop: '2px solid #dfe1e6' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#172b4d', mb: 0.5, fontSize: '1.5rem' }}>
              Loans Details
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b778c', fontSize: '0.875rem' }}>
              Monthly loan EMI status and payment details
            </Typography>
          </Box>

          {/* Year-Month Filter */}
          <Box sx={{ mb: 3 }}>
            <Card sx={{ p: 2.5, backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dfe1e6', boxShadow: 'none' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#172b4d', fontSize: '0.875rem' }}>
                      Select Month
                    </Typography>
                    <input
                      type='month'
                      value={selectedYearMonth}
                      onChange={handleYearMonthChange}
                      className='form-control'
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        border: '1px solid #dfe1e6',
                        fontSize: '14px',
                        color: '#172b4d',
                        backgroundColor: '#fafbfc'
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Box>

          {/* Loans Summary Boxes */}
          <Grid container spacing={2.5} sx={{ mb: 4 }}>
            <LoanSummaryBox
              title="Loans EMI Not Paid"
              count={loansData.notPaid}
              category="not_paid"
              icon="⏱️"
              iconColor="#dce4ea"
              subtitle="EMI payments pending"
            />
            <LoanSummaryBox
              title="Loans EMI Partially Paid"
              count={loansData.partiallyPaid}
              category="partially_paid"
              icon="⚠️"
              iconColor="#dce4ea"
              subtitle="Partial EMI payments"
            />
            <LoanSummaryBox
              title="Fully Paid"
              count={loansData.fullyPaid}
              category="fully_paid"
              icon="✅"
              iconColor="#dce4ea"
              subtitle="Complete EMI payments"
              subCategories={[
                { label: 'Enach', count: loansData.enach, category: 'enach' },
                { label: 'Dynamic QR', count: loansData.dynamicQR, category: 'dynamic_qr' },
                { label: 'Static QR', count: loansData.staticQR, category: 'static_qr' },
                { label: 'Other', count: loansData.other, category: 'other' }
              ]}
            />
          </Grid>

          {/* Loans List Table */}
          {showLoansList && (
            <Box sx={{ mt: 3 }}>
              <Card sx={{ borderRadius: '8px', border: '1px solid #dfe1e6', boxShadow: 'none', overflow: 'hidden' }}>
                <Box sx={{ p: 2.5, backgroundColor: '#fafbfc', borderBottom: '1px solid #dfe1e6' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#172b4d', fontSize: '1rem' }}>
                    {selectedLoanCategory ? selectedLoanCategory.replace(/_/g, ' ').toUpperCase() : 'Loan Details'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b778c', mt: 0.5, fontSize: '0.875rem' }}>
                    Total Records: {loansTotalRows}
                  </Typography>
                </Box>
                <Box sx={{ p: 0 }}>
                  <DataTable
                    columns={loanColumns}
                    data={loansList}
                    pagination
                    paginationServer
                    paginationTotalRows={loansTotalRows}
                    paginationPerPage={loansPageSize}
                    onChangeRowsPerPage={handleLoansPerRowsChange}
                    paginationDefaultPage={loansCurrentPage}
                    onChangePage={handleLoansPageChange}
                    customStyles={{
                      headRow: {
                        style: {
                          backgroundColor: '#fafbfc',
                          borderBottom: '1px solid #dfe1e6',
                          fontWeight: 600,
                          fontSize: '13px',
                          color: '#172b4d',
                          minHeight: '42px'
                        },
                      },
                      rows: {
                        style: {
                          fontSize: '13px',
                          color: '#172b4d',
                          minHeight: '48px',
                          borderBottom: '1px solid #f4f5f7',
                          '&:hover': {
                            backgroundColor: '#f4f5f7',
                            cursor: 'pointer'
                          }
                        },
                      },
                    }}
                  />
                </Box>
              </Card>
            </Box>
          )}
        </Box>
      </Container>

      {/* Filter Modal */}
      <Dialog open={filterModalOpen} onClose={handleCloseFilterModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#0052cc', color: '#fff', py: 2 }}>
          Global Filters
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Location"
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                variant="outlined"
                size="small"
              >
                <MenuItem value="">All Locations</MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location.location_id} value={location.location_id}>
                    {location.location_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="RM (Regional Manager / Regional Head / Cluster Head)"
                value={selectedRM}
                onChange={(e) => setSelectedRM(e.target.value)}
                variant="outlined"
                size="small"
              >
                <MenuItem value="">All RMs</MenuItem>
                {rms.map((rm) => (
                  <MenuItem key={rm.user_id} value={rm.user_id}>
                    {rm.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="User"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                variant="outlined"
                size="small"
              >
                <MenuItem value="">All Users</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.user_id} value={user.user_id}>
                    {user.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={handleResetFilter}
            variant="outlined"
            sx={{
              borderColor: '#dfe1e6',
              color: '#172b4d',
              '&:hover': { borderColor: '#0052cc', backgroundColor: '#f4f5f7' }
            }}
          >
            Reset
          </Button>
          <Button
            onClick={handleApplyFilter}
            variant="contained"
            sx={{
              backgroundColor: '#0052cc',
              '&:hover': { backgroundColor: '#0747a6' }
            }}
          >
            Search
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

function mapStateToProps(state) {
  return {
    userInfo: state.userInfo
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Object.assign(dashboardActions.default), dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(LeadSummaryDashboard);
